import type { DeepPartial, Dictionary } from '../i18n-service.js';

/**
 * fr.ts — French string dictionary.
 *
 * Typed as `DeepPartial<Dictionary>`: any key omitted here falls back to the
 * canonical English value at load time, so a partially translated locale still
 * renders completely rather than showing blanks.
 */
export const fr: DeepPartial<Dictionary> = {
searchBox: {
        placeholder: 'Saisissez un terme de recherche',
        clearLabel: 'Effacer',
    },
    resultsMeta: {
        summary: '{count:number} résultats trouvés en {seconds} secondes',
    },
    pagination: {
        navLabel: 'Pagination des résultats de recherche',
        previous: 'Précédent',
        next: 'Suivant',
    },
    facetPanel: {
        heading: 'Filtres',
        removeLabel: 'Supprimer',
        clear: 'Effacer les filtres',
    },
    viewSwitcher: {
        cards: 'Cartes',
        grid: 'Grille',
        table: 'Tableau',
    },
    userToolbar: {
        add: 'Ajouter des utilisateurs',
        email: 'Courriel',
        copy: 'Copier',
        export: 'Exporter',
        addModal: {
            title: 'Ajouter des utilisateurs',
            closeLabel: 'Fermer',
            tabExisting: 'Rechercher des utilisateurs existants',
            tabNew: 'Vérifier et ajouter des utilisateurs',
            tabOutlook: 'Coller depuis Outlook',
            tabExcel: 'Coller depuis Excel',
            searchPlaceholder: 'Rechercher des utilisateurs…',
            firstName: 'Prénom',
            lastName: 'Nom',
            email: 'Courriel',
            addRow: 'Ajouter une ligne',
            removeRow: 'Supprimer la ligne',
            outlookHint: 'Collez les noms/adresses copiés depuis Outlook (séparés par des points-virgules ou des sauts de ligne).',
            excelHint: 'Collez les lignes depuis Excel : Prénom, Nom, Courriel (séparés par des tabulations).',
            queueSummary: '{count:number} utilisateur(s) en attente de vérification.',
            reviewQueue: 'Vérifier la file d’attente ({count:number})',
            addToQueueReview: 'Ajouter à la file d’attente et vérifier ({count:number})',
            addToQueue: 'Ajouter à la file d’attente ({count:number})',
            addingToQueue: 'Ajout de {count:number} utilisateur(s) à la file d’attente…',
            addedToQueue: '{count:number} utilisateur(s) ajouté(s) à la file d’attente.',
            reviewSubmit: 'Ajouter {count:number} utilisateur(s) au groupe',
            processing: 'Traitement de {current:number} sur {total:number}…',
            cancel: 'Annuler',
            close: 'Fermer',
            submit: 'Ajouter',
            discard: {
                title: 'Abandonner les utilisateurs en attente ?',
                body: 'Vous avez {count:number} utilisateur(s) en attente de vérification qui n’ont pas encore été ajoutés. Si vous fermez maintenant, ils seront supprimés de la file d’attente. Voulez-vous vraiment fermer ?',
                confirm: 'Abandonner et fermer',
                cancel: 'Continuer la modification',
            },
            status: {
                column: 'État',
                checking: 'Vérification…',
                invite: 'Inviter l’utilisateur',
                addToGroup: 'Ajouter au groupe',
                alreadyInGroup: 'Déjà membre du groupe',
                inviting: 'Invitation…',
                adding: 'Ajout…',
                invited: 'Invité',
                added: 'Ajouté',
                error: 'Erreur',
            },
            validation: {
                required: 'Ce champ est obligatoire.',
                invalidEmail: 'Saisissez une adresse courriel valide.',
            },
        },
        search: {
            searching: 'Recherche…',
            noResults: 'Aucun utilisateur correspondant.',
            failed: 'Échec de la recherche.',
            add: 'Ajouter à la file',
            queued: 'En attente',
            added: 'Ajouté',
            queueHint: 'Ajouter {name} à la file d’attente pour vérification',
        },
        toast: {
            loadMembersFailed: 'Impossible de charger les membres du groupe.',
            noEmails: 'Aucune adresse courriel disponible.',
            copySucceeded: '{count:number} adresse(s) copiée(s) dans le presse-papiers.',
            copyFailed: 'Impossible de copier dans le presse-papiers.',
            exportEmpty: 'Aucun utilisateur n’a été retourné pour l’exportation.',
            exportSucceeded: 'L’exportation des utilisateurs a été téléchargée.',
            exportFailed: 'Échec de l’exportation des utilisateurs.',
            userAdded: 'L’utilisateur a été ajouté au groupe.',
            userQueued: 'L’utilisateur a été ajouté à la file d’attente pour vérification.',
            addUserFailed: 'Échec de l’ajout de l’utilisateur au groupe.',
            noUsersToAdd: 'Aucun utilisateur à ajouter.',
            usersAdded: '{count:number} utilisateur(s) ajouté(s).',
            addUsersFailed: 'Échec de l’ajout des utilisateurs.',
            usersAddedPartial: '{added:number} utilisateur(s) ajouté(s) sur {total:number}; {failed:number} échec(s). Vérifiez les lignes mises en évidence.',
        },
        exportFilename: 'Exportation des utilisateurs - {stamp}',
    },
    searchTable: {
        search: {
            loading: {
                status: 'Chargement...',
                message: 'Recherche...',
            },
        },
        results: {
            empty: {
                message: 'Aucun résultat trouvé. Essayez un autre terme de recherche.',
                messageForTerm: 'Aucun résultat trouvé pour « {term} ». Essayez un autre terme de recherche.',
            },
        },
        export: {
            excel: 'Exporter vers Excel',
            pdf: 'Exporter vers PDF',
        },
    },
    memberDirectory: {
        search: {
            loading: {
                status: 'Chargement...',
                message: 'Recherche...',
            },
        },
        facets: {
            jobTitle: 'Titre du poste',
            department: 'Service',
        },
        fields: {
            displayName: 'Nom',
            jobTitle: 'Titre du poste',
            companyName: 'Entreprise',
            department: 'Service',
            mail: 'Courriel',
            chat: 'Conversation',
            businessPhones: 'Téléphone',
            mobilePhone: 'Téléphone mobile',
            linkedIn: 'LinkedIn',
            twitter: 'Twitter',
            youTube: 'YouTube',
            blog: 'Blogue',
            companyWebsite: 'Site Web',
            streetAddress: 'Adresse',
            city: 'Ville',
            state: 'Province/État',
            postalCode: 'Code postal',
            country: 'Pays',
        },
        results: {
            empty: {
                message: 'Aucun résultat trouvé. Essayez un autre terme de recherche.',
                messageForTerm: 'Aucun résultat trouvé pour « {term} ». Essayez un autre terme de recherche.',
            },
        },
        detailsModal: {
            closeLabel: 'Fermer',
            contactHeading: 'Coordonnées',
            viewProfile: 'Afficher le profil',
            moreDetails: 'Plus de détails',
            fewerDetails: 'Moins de détails',
            editUser: 'Modifier l’utilisateur',
            removeUser: 'Supprimer l’utilisateur',
        },
        editModal: {
            closeLabel: 'Fermer',
            loadingStatus: 'Chargement...',
            heading: 'Modifier l’utilisateur',
            headingNamed: 'Modifier l’utilisateur : {name}',
            changePhoto: 'Modifier la photo',
            removeFromGroup: 'Supprimer du groupe',
            save: 'Enregistrer',
            saveAndClose: 'Enregistrer et fermer',
            toggleDropdown: 'Afficher/masquer la liste déroulante',
            close: 'Fermer',
        },
        confirmModal: {
            title: 'Confirmer',
            closeLabel: 'Fermer',
            message: 'Supprimer {name} du groupe actuel ?',
            fallbackName: 'cet utilisateur',
            cancel: 'Annuler',
            confirm: 'Supprimer',
        },
        toast: {
            loadUserFailed: 'Impossible de charger les détails de l’utilisateur.',
            readImageFailed: 'Impossible de lire l’image sélectionnée.',
            noChanges: 'Aucune modification à enregistrer.',
            updateSucceeded: 'L’utilisateur a été mis à jour avec succès.',
            updateFailed: 'Échec de la mise à jour de l’utilisateur.',
            removeSucceeded: 'L’utilisateur a été supprimé du groupe.',
            removeFailed: 'Échec de la suppression de l’utilisateur du groupe.',
        },
    },
    dataroom: {
        shared: {
            loading: 'Chargement...',
            close: 'Fermer',
            notAvailable: 's.o.',
        },
        documents: {
            heading: 'Documents',
            toolbar: {
                upload: 'Téléverser des documents',
                update: 'Mettre à jour les métadonnées des documents',
                download: 'Télécharger tous les documents',
                excel: 'Ouvrir le fichier Excel de configuration de la salle de données',
                sharePoint: 'Ouvrir dans SharePoint',
            },
            details: {
                title: 'Détails du document',
                open: 'Ouvrir',
                documentType: 'Type de document',
                comments: 'Commentaires',
                instructions: 'Instructions',
                lastModified: 'Dernière modification',
            },
            upload: {
                title: 'Téléverser des documents',
                dropFiles: 'Déposez les fichiers à téléverser',
                or: 'ou',
                selectFiles: 'Sélectionner des fichiers',
                addFiles: 'Ajouter des fichiers',
                maxSize: 'Taille maximale d’un fichier à téléverser : 2 Go',
                submit: 'Téléverser',
                cancel: 'Annuler',
            },
            update: {
                title: 'Mettre à jour les métadonnées des documents',
                submit: 'Mettre à jour',
                cancel: 'Annuler',
            },
            status: {
                uploadTitle: 'Téléversement en cours',
                uploadMessage: 'Votre téléversement est en cours. Veuillez patienter...',
                updateTitle: 'Mise à jour en cours',
                updateMessage: 'Votre mise à jour est en cours. Veuillez patienter...',
                downloadTitle: 'Téléchargement en cours',
                downloadMessage: 'Votre téléchargement est en cours. Veuillez patienter...',
                cancel: 'Annuler',
            },
            confirm: {
                title: 'Confirmer',
                cancelUpload: 'Voulez-vous vraiment annuler ce téléversement ?',
                cancelDownload: 'Voulez-vous vraiment annuler ce téléchargement ?',
                no: 'Non',
                yes: 'Oui',
            },
            grid: {
                deleteRow: 'Supprimer cette ligne',
                fieldRequired: 'Le champ {field} est obligatoire',
            },
            columns: {
                document: 'Document',
                lastModified: 'Dernière modification',
            },
            toast: {
                uploadSucceeded: '{count:number} document(s) téléversé(s) avec succès - {names}',
                uploadFailed: '{count:number} document(s) non téléversé(s) - {names}',
                editSucceeded: '{count:number} document(s) modifié(s) avec succès - {names}',
                editFailed: '{count:number} document(s) non modifié(s) - {names}',
                downloadAborted: 'Téléchargement de {filename} annulé',
                downloadCompleted: 'Téléchargement de {filename} terminé',
            },
        },
        properties: {
            heading: 'Propriétés de la salle de données',
            empty: 'Aucune propriété trouvée',
        },
        requested: {
            heading: 'Documents demandés',
            columns: {
                documentType: 'Type de document',
                required: 'Obligatoire',
                instructions: 'Instructions',
            },
        },
        directory: {
            tooltip: 'Create Data Room(FR)',
            formModalTitle: 'Create New Data Room(FR)',
            create: 'Create(FR)',
            close: 'Close(FR)',
            statusModalTitle: 'Creating New Data Room(FR)',
            statusLoading: 'Loading...(FR)',
            statusMessage: 'Creating Data Room. Please wait...(FR)',
            toastSuccess: 'Data Room is being provisioned(FR)',
            toastError: 'An error occurred when trying to create data room(FR)',
        },
    },
};
